import React, { useState } from 'react';
import { Box, Button } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Download as DownloadIcon } from '@mui/icons-material';
import dayjs from 'dayjs';

const MonthlyBreakdownTable = ({ monthlyData, isLoading }) => {
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 12,
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatPercent = (value) => {
    return `${value.toFixed(2)}%`;
  };

  const columns = [
    {
      field: 'displayName',
      headerName: 'Month/Year',
      width: 150,
      headerClassName: 'super-app-theme--header'
    },
    {
      field: 'openingAum',
      headerName: 'Opening AUM',
      width: 150,
      headerClassName: 'super-app-theme--header',
      renderCell: (params) => formatCurrency(params.value)
    },
    {
      field: 'deposits',
      headerName: 'Deposits',
      width: 130,
      headerClassName: 'super-app-theme--header',
      renderCell: (params) => formatCurrency(params.value)
    },
    {
      field: 'withdrawals',
      headerName: 'Withdrawals',
      width: 130,
      headerClassName: 'super-app-theme--header',
      renderCell: (params) => formatCurrency(params.value)
    },
    {
      field: 'netPerformance',
      headerName: 'Net Performance',
      width: 150,
      headerClassName: 'super-app-theme--header',
      renderCell: (params) => formatCurrency(params.value)
    },
    {
      field: 'aum',
      headerName: 'Closing AUM',
      width: 150,
      headerClassName: 'super-app-theme--header',
      renderCell: (params) => formatCurrency(params.value)
    },
    {
      field: 'returnPercent',
      headerName: '% Return',
      width: 100,
      headerClassName: 'super-app-theme--header',
      renderCell: (params) => (
        <span style={{ color: params.value >= 0 ? '#2af598' : '#f52a2d' }}>
          {formatPercent(params.value)}
        </span>
      )
    },
    {
      field: 'activeClients',
      headerName: 'Active Clients',
      width: 120,
      headerClassName: 'super-app-theme--header'
    }
  ];

  // Calculate opening AUM from previous month's closing
  const rows = monthlyData.map((month, index) => {
    const openingAum = index > 0 ? monthlyData[index - 1].aum : month.aum - month.netPerformance - month.deposits + month.withdrawals;
    
    return {
      id: `${month.year}-${month.month}`,
      displayName: `${dayjs().month(month.month).format('MMMM')} ${month.year}`,
      openingAum,
      deposits: month.deposits,
      withdrawals: month.withdrawals,
      netPerformance: month.netPerformance,
      aum: month.aum,
      returnPercent: month.returnPercent || 0,
      activeClients: month.activeClients
    };
  });

  const handleExport = () => {
    // Convert to CSV
    const headers = columns.map(col => col.headerName).join(',');
    const csvRows = rows.map(row => 
      columns.map(col => {
        const value = row[col.field];
        if (typeof value === 'number') {
          return value;
        }
        return `"${value}"`;
      }).join(',')
    );
    
    const csv = [headers, ...csvRows].join('\n');
    
    // Download
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fund-monthly-breakdown-${dayjs().format('YYYY-MM-DD')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleExport}
          sx={{
            background: 'linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)',
            color: '#fff',
            '&:hover': {
              background: 'linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)',
              opacity: 0.9
            }
          }}
        >
          Export CSV
        </Button>
      </Box>
      
      <Box
        sx={{
          height: 600,
          width: '100%',
          '& .super-app-theme--header': {
            backgroundColor: 'rgba(42, 245, 152, 0.1)',
            color: '#fff',
            fontWeight: '600'
          },
          '& .MuiDataGrid-root': {
            border: '1px solid rgba(255, 255, 255, 0.1)',
            color: '#fff'
          },
          '& .MuiDataGrid-cell': {
            borderColor: 'rgba(255, 255, 255, 0.1)'
          },
          '& .MuiDataGrid-columnHeaders': {
            borderColor: 'rgba(255, 255, 255, 0.1)'
          },
          '& .MuiDataGrid-footerContainer': {
            borderColor: 'rgba(255, 255, 255, 0.1)',
            color: '#fff'
          },
          '& .MuiTablePagination-root': {
            color: '#fff'
          },
          '& .MuiIconButton-root': {
            color: '#fff'
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: 'rgba(42, 245, 152, 0.05)'
          }
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[12, 24, 50]}
          loading={isLoading}
          disableRowSelectionOnClick
          sx={{
            '& .MuiDataGrid-virtualScroller': {
              backgroundColor: 'transparent'
            }
          }}
        />
      </Box>
    </Box>
  );
};

export default MonthlyBreakdownTable;

